import React, { useEffect, useState } from "react";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import {
  GetCourseByIdDocument,
  StoragePathType,
  useCreateModuleMutation,
  useUpdateModuleMutation,
} from "../../../graphql/generated/graphql";
import { CustomCourseworkModulesViewModalState, CourseEditPageModalState } from "../../../contexts/ActiveCourseContext";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { StyleSheet, View, ActivityIndicator } from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import ImageUpload from "../../common/general/ImageUpload/ImageUpload";
import Sentry from "../../../utils/sentry";

const moduleSchema = z.object({
  moduleName: z.string().min(1, "Module name is required"),
  description: z.string().optional(),
  thumbnail: z.string().optional(),
});

type ModuleFormData = z.infer<typeof moduleSchema>;

interface AddModuleModalProps {
  visible: boolean;
  setModalState: (state: CourseEditPageModalState) => void;
  courseId: string;
  modalState: CourseEditPageModalState;
}

const AddModuleModalContainer = ({ visible, setModalState, courseId, modalState }: AddModuleModalProps) => {
  const { dispatch } = useAppState();
  const { isMedium } = useResponsiveScreen();

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [createModuleMutation] = useCreateModuleMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [updateModuleMutation] = useUpdateModuleMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
    watch,
  } = useForm<ModuleFormData>({
    resolver: zodResolver(moduleSchema),
    defaultValues: {
      moduleName: "",
      description: "",
      thumbnail: "",
    },
  });

  const handleClose = () => {
    reset();
    setUploadError(null);
    setModalState({
      ...modalState,
      state: CustomCourseworkModulesViewModalState.CLOSED,
      resourceType: null,
      resourceId: null,
    });
  };

  useEffect(() => {
    // Reset form when modal closes
    if (!visible) {
      reset();
      setUploadError(null);
      return;
    }

    // Only populate if we're editing (have resourceId) and have activeModule
    if (modalState.resourceId && modalState.activeModule) {
      setValue("moduleName", modalState.activeModule.title || "");
      setValue("description", modalState.activeModule.description || "");
      setValue("thumbnail", modalState.activeModule.thumbnail || "");
    } else if (!modalState.resourceId) {
      // If we're adding a new module, ensure form is reset
      reset();
    }
  }, [modalState.resourceId, modalState.activeModule, visible, setValue, reset]);

  const onSubmit = async (data: ModuleFormData) => {
    try {
      setIsProcessing(true);

      if (modalState.resourceId && modalState.activeModule) {
        const response = await updateModuleMutation({
          variables: {
            id: modalState.resourceId,
            title: data.moduleName,
            description: data.description || null,
            thumbnail: data.thumbnail || null,
          },
        });

        if (response?.data?.updateModule?.success) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });
        } else {
          throw new Error(response?.data?.updateModule?.message || "Failed to update module");
        }
      } else {
        const response = await createModuleMutation({
          variables: {
            title: data.moduleName,
            parentCourseId: courseId,
            description: data.description || null,
            thumbnail: data.thumbnail || null,
          },
        });

        if (response?.data?.createModule?.success) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });
        } else {
          throw new Error(response?.data?.createModule?.message || "Failed to create module");
        }
      }
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <CustomAdminModal
      visible={visible}
      onRequestClose={handleClose}
      title={modalState.resourceId ? "Edit Module" : "Add Module"}
      style={{ width: isMedium ? "60%" : "90%" }}>
      <View style={styles.content}>
        <Controller
          control={control}
          name="moduleName"
          render={({ field: { value, onChange } }) => (
            <>
              <CustomText style={styles.label} text="Module Name" />
              <CustomTextInput
                style={styles.input}
                value={value}
                placeholder="Enter module name"
                onChangeText={onChange}
                accessibilityHint="Enter your module name"
              />
              {errors?.moduleName?.message && (
                <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.moduleName?.message} />
              )}
            </>
          )}
        />

        <CustomSpacing type="vertical" size="l" />

        <Controller
          control={control}
          name="description"
          render={({ field: { value, onChange } }) => (
            <>
              <CustomText style={styles.label} text="Module Description" />
              <CustomTextInput
                style={[styles.input, styles.textArea]}
                value={value}
                placeholder="Enter module description (optional)"
                onChangeText={onChange}
                multiline
                numberOfLines={4}
                accessibilityHint="Enter module description"
              />
              {errors?.description?.message && (
                <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.description?.message} />
              )}
            </>
          )}
        />

        <CustomSpacing type="vertical" size="l" />

        <View>
          <CustomText style={styles.label} text="Upload Thumbnail (Optional)" />
          <CustomSpacing type="vertical" size="s" />

          <Controller
            control={control}
            name="thumbnail"
            render={({ field: { onChange, value } }) => (
              <View style={styles.thumbnailSection}>
                <ImageUpload
                  value={value}
                  onChange={onChange}
                  storagePath={StoragePathType.Thumbnails}
                  containerStyle={styles.thumbnailUpload}
                  imageStyle={styles.thumbnailImage}
                  aspectRatio={[16, 9]}
                  allowsEditing={true}
                  buttonText="Upload Thumbnail"
                  changeButtonText="Change Thumbnail"
                  dropzoneText="Drag & drop module thumbnail here"
                  dropzoneSubtext=""
                  onError={(error) => setUploadError(error)}
                />
              </View>
            )}
          />

          {uploadError && (
            <>
              <CustomSpacing type="vertical" size="s" />
              <CustomText text={uploadError} size="xs" style={styles.errorText} useTranslationText={false} />
            </>
          )}

          <CustomSpacing type="vertical" size="xs" />
          <CustomText
            text="Upload an image for the module. JPG, PNG, or WebP, max 2MB."
            size="xs"
            style={styles.helperText}
            useTranslationText={false}
          />
        </View>

        <CustomSpacing type="vertical" size="xl" />

        <View style={styles.buttonContainer}>
          <CustomButton text="Cancel" onPress={handleClose} styleType="primaryLight" size="m" disabled={isProcessing} />
          <CustomButton
            text={isProcessing ? "Saving..." : "Save"}
            onPress={handleSubmit(onSubmit)}
            styleType="primary"
            style={!isValid || isProcessing ? { backgroundColor: "#ccc" } : {}}
            size="m"
            disabled={!isValid || isProcessing}
            leftIcon={isProcessing ? <ActivityIndicator size="small" color="#FFFFFF" /> : undefined}
          />
        </View>
      </View>
    </CustomAdminModal>
  );
};

export default AddModuleModalContainer;

const styles = StyleSheet.create({
  content: {
    padding: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1A1A1A",
    marginBottom: 8,
  },
  input: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E5E5",
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  textArea: {
    height: 100,
    textAlignVertical: "top",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 16,
  },
  thumbnailSection: {
    alignItems: "flex-start",
    width: "100%",
  },
  thumbnailUpload: {
    width: 300,
    height: 168, // 16:9 aspect ratio
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E5E5",
    backgroundColor: "#F9FAFB",
    justifyContent: "center",
    alignItems: "center",
  },
  thumbnailImage: {
    borderRadius: 8,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
  },
  errorText: {
    color: "#EF4444",
  },
  helperText: {
    color: "#6B7280",
  },
});
